import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import Done from '@material-ui/icons/Done';
import { Button, Grid, Box, Typography, makeStyles } from '@material-ui/core';
import { Input, Select, StarsRating } from 'components/FormInputs';
import moment from 'logic/moment';
import { useFormik } from 'components/shared/Formik';
import { validate } from './validate';

const years = new Array(5).fill(null).map((value, index) => moment().format('Y') - index);

const rating = [1, 2, 3, 4, 5];

const styles = makeStyles((theme) => ({
  appBar: {
    position: 'relative',
  },
  button: {
    marginTop: theme.spacing(2),
  },
  center: {
    textAlign: 'center',
    '& p': {
      textAlign: 'center',
    },
  },
}));

const AddReviewForm = ({ isUpdating, initialValues, sendReview }) => {
  const classes = styles();
  const intl = useIntl();
  const formik = useFormik({
    validate: validate({ intl }),
    initialValues,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values) => {
      return sendReview(values);
    },
  });

  const {
    values,
    errors,
    setFieldValue,
    handleSubmit,
    handleChange,
    handleBlur,
    isValid,
    isSubmitting,
    pristine,
  } = formik;

  return (
    <>
      <form className={classes.edit} onSubmit={handleSubmit} noValidate>
        <Grid container spacing={2} className={classes.fields}>
          <Grid item xs={12} md={6}>
            <Select
              name="year"
              value={values.year}
              onChange={handleChange}
              onBlur={handleBlur}
              errors={errors}
              options={years.map((year) => ({ label: year, value: String(year) }))}
              disabled={isUpdating}
              required
            />
          </Grid>
          <Grid item xs={12}>
            &nbsp;
          </Grid>
          <Grid item xs={12} sm={6} className={classes.center}>
            <StarsRating
              label={
                <FormattedMessage
                  id="reviews.score"
                  defaultMessage="How was your overall experience?"
                />
              }
              name="score"
              value={values.score}
              disabled={isUpdating}
              scores={rating}
              onChange={(v) => {
                setFieldValue('score', v);
              }}
              errors={errors}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6} className={classes.center}>
            <StarsRating
              label={
                <FormattedMessage
                  id="reviews.accuracy"
                  defaultMessage="How well did the ad match reality?"
                />
              }
              name="accuracy"
              value={values.accuracy}
              disabled={isUpdating}
              scores={rating}
              onChange={(v) => setFieldValue('accuracy', v)}
              errors={errors}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6} className={classes.center}>
            <StarsRating
              label={
                <FormattedMessage
                  id="reviews.cleanliness"
                  defaultMessage="How good was the cleaning condition?"
                />
              }
              name="cleanliness"
              value={values.cleanliness}
              disabled={isUpdating}
              scores={rating}
              onChange={(v) => setFieldValue('cleanliness', v)}
              errors={errors}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6} className={classes.center}>
            <StarsRating
              label={
                <FormattedMessage
                  id="reviews.location"
                  defaultMessage="How good is the location of this cottage?"
                />
              }
              name="location"
              value={values.location}
              disabled={isUpdating}
              scores={rating}
              onChange={(v) => setFieldValue('location', v)}
              errors={errors}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <Input
              name="body"
              value={values.body}
              onChange={handleChange}
              onBlur={handleBlur}
              errors={errors}
              maxLength={1000}
              disabled={isUpdating}
              variant="outlined"
              placeholder={intl.formatMessage({
                id: 'Add additional text for the review',
                defaultMessage: 'Add additional text for the review',
              })}
              required
              multiline
              rows={5}
            />
            <Box textAlign="right">
              <Typography variant="body2" color={values.body?.length > 1000 ? 'error' : 'initial'}>
                {values.body?.length || 0}/1000
              </Typography>
            </Box>
          </Grid>
        </Grid>
        <div className={classes.center}>
          <Button
            color="primary"
            type="submit"
            variant="contained"
            disabled={isUpdating || pristine || isSubmitting || !isValid}
            className={classes.button}
          >
            <Done />
            <FormattedMessage id="Send" defaultMessage="Send" />
          </Button>
        </div>
      </form>
    </>
  );
};

AddReviewForm.propTypes = {
  isUpdating: PropTypes.bool,
  initialValues: PropTypes.shape({}),
  sendReview: PropTypes.func.isRequired,
};

export default AddReviewForm;
